import { TFunction } from "next-i18next";
import { withCdnUrl } from "@smile2impress/attraction-utils";
import { UTSLocaleToCountryVersion } from "@smile2impress/attraction-constants";

import { PageContentProps } from "content/content.types";
import { mergeContent } from "utils/mergeContent";
import { PRIVACY_POLICY_LINKS_BY_LOCALE, UrlsConfig } from "configs/urlsConfig";
import { SocialTypes } from "constants/enum";
import contacts from "configs/contacts";

import defaultContent from "./common";

const pageContent = (t: TFunction, { locale }: PageContentProps) => {
    const content = defaultContent(t, { locale });

    return mergeContent(content, {
        header: {
            menuTitle: t("navigationMenuRelaunch:navigationMenu"),
            logoLink: UrlsConfig.MAIN,
            items: [
                {
                    text: t("navigationMenuRelaunch:navigationTreatments"),
                    highlighted: true,
                    items: [
                        {
                            text: t(
                                "navigationMenuRelaunch:navigationAligners"
                            ),
                            link: UrlsConfig.ALIGNERS,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/aligners.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/aligners.png"
                                ),
                            },
                        },
                        {
                            text: t(
                                "navigationMenuRelaunch:navigationRetainers"
                            ),
                            link: UrlsConfig.RETAINERS_SUBSCRIPTION,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/retainers.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/retainers.png"
                                ),
                            },
                        },
                    ],
                },
                {
                    text: t("navigationMenuRelaunch:navigationWhitening"),
                    link: UrlsConfig.WHITENING,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/whitening.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/whitening.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationWhy"),
                    link: UrlsConfig.WHY_IMPRESS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/whyImpress.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/whyImpress.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationPricing"),
                    link: UrlsConfig.PRICES,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/prices.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/prices.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationClinics"),
                    link: UrlsConfig.LOCATIONS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/aligners/steps-to-treatment-3.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/aligners/steps-to-treatment-3.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationResults"),
                    link: UrlsConfig.RESULTS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/aligners/steps-to-treatment-3.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/aligners/steps-to-treatment-3.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationBlog"),
                    link: UrlsConfig.BLOG,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/blog.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/blog.png"
                        ),
                    },
                },
            ],
        },
        footer: {
            company: {
                links: [
                    ...content.footer.company.links,
                    {
                        id: "studentbeans",
                        name: t("navigationMenuRelaunch:studentBeans"),
                        link: UrlsConfig.STUDENT_BEANS,
                    },
                ],
            },
            product: {
                title: t("global:productFooterText"),
                links: [
                    {
                        id: "aligners",
                        name: t("global:alignersPageTitle"),
                        link: UrlsConfig.ALIGNERS,
                    },
                    {
                        id: "kids",
                        name: t("global:kidsPageTitle"),
                        link: UrlsConfig.KIDS,
                    },
                    {
                        id: "retainers",
                        name: t("global:nightRetainersPageTitle"),
                        link: UrlsConfig.RETAINERS_SUBSCRIPTION,
                    },
                    {
                        id: "whitening",
                        name: t("global:whiteningPageTitle"),
                        link: UrlsConfig.WHITENING,
                    },
                    {
                        id: "malocclusion",
                        name: t("navigationMenuRelaunch:malocclusion"),
                        link: UrlsConfig.MALOCCLUSION,
                    },
                    {
                        id: "diastema",
                        name: t("navigationMenuRelaunch:clusterBDiastema"),
                        link: UrlsConfig.DIASTEMA,
                    },
                    {
                        id: "crossbite",
                        name: t("navigationMenuRelaunch:clusterBCrossbite"),
                        link: UrlsConfig.CROSSBITE,
                    },
                ],
            },
            legal: {
                title: t("global:legalNoticeFooterTitle"),
                links: [
                    {
                        id: "privacyPolicyPage",
                        name: t("global:privacyPolicy"),
                        link: PRIVACY_POLICY_LINKS_BY_LOCALE[locale],
                    },
                    {
                        id: "cookiesPage",
                        name: t("global:cookiesPolicy"),
                        link: withCdnUrl(
                            `${UrlsConfig.POLICY}/cookies-policy_${
                                UTSLocaleToCountryVersion[
                                    locale as keyof typeof UTSLocaleToCountryVersion
                                ]
                            }.pdf`
                        ),
                    },
                    {
                        id: "legalNoticePage",
                        name: t("global:legalNoticeFooterText"),
                        link: "https://faq.smile2impress.com/hubfs/Attraction%20documents/mention_legale_france_impress.pdf",
                    },
                ],
            },
            support: {
                title: t("global:supportFooterText"),
                links: content.footer.support.links.map((link) => {
                    if (link.id === SocialTypes.WA) {
                        link.link = (
                            contacts.whatsapp[locale] as any
                        ).newPatientsLink;
                    }
                    return link;
                }),
            },
        },
    });
};

export default pageContent;
